/* eslint-disable no-param-reassign, max-lines, max-params */
import { useDtfStore } from "components/jiffy/vue-product-details-dtf/store";

import { objNamesToSnakeCase, objNamesToCamelCase } from "common/utils/helpers/cases";

const initialState = {
  index: 0,
  artworkSize: {
    width: 0,
    height: 0,
    widthInch: 0,
    heightInch: 0
  },
  editType: null,
  side: null,
  urlCache: new Map()
};

const artworkModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  getters: {
    removeBackground: (_, getters) => getters.artworkCamelCase?.removeBackground,
    artworkUrl: (state, { artworkCamelCase: artwork }) => {
      const tryToFindImageSource = () => {
        if (!artwork) {
          return URL.createObjectURL(new Blob());
        }

        if (artwork.isFile && artwork.file) {
          if (artwork?.file?.type === "application/pdf") {
            return null;
          }

          const fileKey = artwork.file.name + artwork.file.lastModified;
          if (state.urlCache.has(fileKey)) {
            return state.urlCache.get(fileKey);
          }

          const url = URL.createObjectURL(artwork?.file || new Blob());
          return url;
        }

        if (artwork.enhancementCompleted) {
          if (
            artwork.enhancementTier === "print_original" ||
            artwork.enhancementTier === "custom"
          ) {
            return artwork.originalImageUrl || artwork.url;
          }

          if (artwork.basicPipelineData && artwork.enhancementTier === "basic") {
            return artwork.removeBackground
              ? artwork.basicPipelineData.url
              : artwork.basicPipelineData.withBgUrl;
          }

          return artwork.removeBackground
            ? artwork.noBgUrl
            : artwork.enhancedWithBgUrl || artwork.noBgNoTrimUrl;
        }

        return artwork.originalImageUrl;
      };

      return tryToFindImageSource() || artwork?.url;
    },
    uploadArtwork: (_, getters) => Boolean(getters.artworkCamelCase?.isLoading),
    comment: (_, getters) => getters.artworkCamelCase?.enhancementNote,
    enhanced: (_, getters) => Boolean(getters.artworkCamelCase?.enhancementCompleted),
    uploadErrorText: (_, getters) => getters.artworkCamelCase?.uploadError,
    artworkSize: state => state.artworkSize,
    artworkCamelCase: state => {
      const dtfStore = useDtfStore();
      const { artworks } = dtfStore;

      return artworks[state.index];
    },
    artwork: (_, getters) => {
      if (!getters.artworkCamelCase) {
        return null;
      }

      const snakeCaseArtwork = objNamesToSnakeCase(getters.artworkCamelCase);
      snakeCaseArtwork.no_bg_url ??= snakeCaseArtwork.enhanced_no_bg_url;
      snakeCaseArtwork.with_bg_url ??= snakeCaseArtwork.enhanced_with_bg_url;

      return snakeCaseArtwork;
    },
    artworkExists: (_, getters) => !!getters.artwork && !getters.artworkCamelCase?.isSimulated,
    serializedArtwork: (_, getters) => {
      if (!getters.artwork || getters.artworkCamelCase?.isSimulated) return null;

      return {
        dtf_artwork: {
          id: getters.artwork.id,
          enhance: getters.enhanced,
          remove_background: Boolean(getters.removeBackground),
          comment: getters.comment,
          enhancement_tier: getters.artworkCamelCase.enhancementTier
        },
        width: getters.artworkSize.width,
        height: getters.artworkSize.height,
        position: ""
      };
    },
    isConnectionSetup: () => {
      const dtfStore = useDtfStore();
      return Boolean(dtfStore.dtfChannelUuid);
    },
    editType: state => state.editType,
    isValid: (_, getters) => {
      const hasValidDimensions =
        Boolean(getters.serializedArtwork?.width) && Boolean(getters.serializedArtwork?.height);
      const hasArtworkUrl = Boolean(getters.artworkUrl);
      return hasValidDimensions && hasArtworkUrl;
    }
  },

  mutations: {
    setRemoveBackground(state, isRemoved) {
      const dtfStore = useDtfStore();
      dtfStore.updateArtworkValues({ index: state.index, removeBackground: isRemoved });
    },
    setArtworkSize(state, size) {
      state.artworkSize = size;
      const dtfStore = useDtfStore();
      dtfStore.updateArtworkValues({
        index: state.index,
        isSizesEdited: true,
        width: size.widthInch,
        height: size.heightInch
      });
      const artworkId = dtfStore.artworks[state.index].id;
      dtfStore.updatePreviewDpi({ artworkId });
      dtfStore.downscaleOriginalImage({ artworkId });
    },
    clearArtworkSize(state) {
      state.artworkSize = { width: null, height: null, widthInch: 0, heightInch: 0 };
    },
    setArtworkComment(state, comment) {
      const dtfStore = useDtfStore();
      dtfStore.updateArtworkValues({
        index: state.index,
        enhancementNote: (comment ?? "").trim()
      });
    },
    setEditType(state, value) {
      state.editType = value;
    },
    setMode(state, value /* front or back */) {
      if (value === "front") {
        state.index = 0;
      } else {
        state.index = 1;
      }
    },
    setSide(state, side) {
      state.side = side;
    },
    setUrlCache(state, { key, url }) {
      state.urlCache.set(key, url);
    },
    clearUrlCache(state) {
      state.urlCache.forEach(url => URL.revokeObjectURL(url));
      state.urlCache.clear();
    }
  },

  actions: {
    /* eslint-disable max-lines-per-function */
    upload({ state, commit }, originalFile) {
      const dtfStore = useDtfStore();

      // Create and cache the URL when the file is uploaded
      const fileKey = originalFile.name + originalFile.lastModified;
      const url = URL.createObjectURL(originalFile);
      commit("setUrlCache", { key: fileKey, url });

      dtfStore.addFilesToQueue({
        files: [originalFile],
        isDropEvent: false,
        artworkIndex: state.index
      });
    },
    async restore({ state, commit, rootGetters }, placement) {
      const dtfStore = useDtfStore();
      const {
        width,
        height,
        dtf_artwork: dtfArtwork,
        enhancement_request_note: enhancementNote
      } = placement;
      const artwork = objNamesToCamelCase(dtfArtwork);

      const pxWidth = rootGetters[`surface_${state.side}/convertInchesToPx`](width);
      const pxHeight = rootGetters[`surface_${state.side}/convertInchesToPx`](height);

      dtfStore.setLoadedArtwork({
        index: state.index,
        artwork: {
          ...artwork,
          originalDpi: artwork.enhanceImage
            ? artwork.enhancedOriginalDpi
            : artwork.originalMetadata?.dpi,
          success: true,
          width,
          height
        }
      });

      dtfStore.updateArtworkValuesById({
        id: artwork.id,
        enhancementCompleted: true,
        enhancedDpi: artwork.metadata?.dpi,
        originalImageUrl: artwork.originalImageUrl,
        enhancedWithBgUrl: artwork.withBgUrl,
        enhancedNoBgUrl: artwork.noBgUrl,
        enhanceImage: artwork.enhanceImage,
        enhancementTier: artwork.enhancementTier,
        enhancementNote: enhancementNote || ""
      });

      commit("setArtworkSize", {
        width: pxWidth,
        height: pxHeight,
        widthInch: width,
        heightInch: height
      });
    },
    removeArtwork({ state, getters, commit }) {
      const dtfStore = useDtfStore();

      dtfStore.removeArtworkWithActivity(getters.artwork.id);

      // Clear URL cache when removing artwork
      commit("clearUrlCache");

      // The dtf store is using filter during remove
      // When we remove the front artwork, then the back artwork will take the first place
      // So we need to add a new front artwork simulation to the front of the array
      if (state.index === 0) {
        dtfStore.$state.artworksState.unshift({
          ...dtfStore.simulation,
          index: state.index,
          id: state.index
        });
      } else if (state.index === 1) {
        dtfStore.$state.artworksState.push({
          ...dtfStore.simulation,
          index: state.index,
          id: state.index
        });
      }

      Object.assign(state, {
        ...initialState,
        index: state.index,
        id: state.index
      });

      dtfStore.getArtworksPricing();
      dtfStore.updatePdpDates();
    }
  }
};

export default artworkModule;
